/**
 * Streaming client for an agent chat turn.
 *
 * POSTs the turn to the unified `/agents` endpoint and parses the
 * `text/event-stream` response, dispatching each decoded frame to a handler.
 * Auth is a Cognito **access token** (from Amplify) sent both as the
 * `Authorization: Bearer` header and duplicated in `body._jwt` — AgentCore
 * strips the header before invoking the container, so the container's verifier
 * reads the body copy.
 *
 * Transport: SSE, blank-line delimited frames, `data:`-prefixed JSON each
 * carrying a `type` discriminator, terminated by exactly one `complete` or
 * `error` frame; `: ` comment frames are keepalives.
 *
 * Event shapes mirror the runtime's `RunStreamEvent` union verbatim
 * (`packages/runtime/src/runner.ts`), which the server forwards as-is except
 * `complete`, whose `final` it wraps in the `/invocations` JSON envelope. The
 * server also injects `memory-write` frames (a transport concern, not part of
 * `RunStreamEvent`): the turn's buffered memory mutations, flushed just before
 * the terminal `complete` so the memory graph can update from the turn stream.
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { agentConfig } from '../config/agent-config.ts';
import type { WorkflowRunRecord } from './workflowsApi.ts';

/**
 * Thrown when the agent endpoint rejects the turn with `401` — the signed-in
 * session is dead (refresh token revoked/expired) despite a token having been
 * sent. A distinct type so callers can branch on identity rather than parsing
 * the message: the console treats it as "drop to sign-in" rather than a generic
 * retryable error. Permission/scope failures (`403`/`424`) are *not* this — they
 * keep the session and ask the user to re-authenticate manually.
 */
export class SessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Why a stream stopped delivering. A closed set mapped to the throw sites
 * below — anything outside it would be a programming error, so the type
 * forbids it rather than a runtime branch guarding against it.
 */
export type StreamFailureReason =
  | 'stalled-no-bytes'
  | 'stalled-no-progress'
  | 'transport-dropped'
  | 'eof-no-terminal'
  | 'turn-duplicate';

/**
 * What the stream actually did before it died.
 *
 * Rides on {@link StreamStalledError} so the CONSUMER can report the diagnosis
 * alongside the recovery verdict: this module knows what happened but not
 * whether the user ended up seeing their answer, and the hook that runs
 * recovery knows the verdict but — by then — nothing about the stream.
 *
 * Optional fields are OMITTED rather than sentinel-valued. A `-1` for "no
 * frames arrived" averages into any `stats avg(...)` query and quietly drags
 * the answer below the truth; an absent field does not. "No frames" stays
 * queryable as `frameCount = 0`.
 */
export interface StreamStats {
  readonly reason: StreamFailureReason;
  /** ms from request start to the failure. */
  readonly elapsedMs: number;
  /** Decoded event counts by `type`. Keepalives and malformed frames excluded. */
  readonly frames: Readonly<Record<string, number>>;
  /** Total decoded events (the sum of `frames`). */
  readonly frameCount: number;
  /** `: ping` liveness beats seen. High keepalives + zero frames = a live
   *  socket carrying a stuck turn, which looks identical to a fast one without
   *  this. */
  readonly keepalives: number;
  /** Frames that carried `data:` we could not decode — a runtime defect, and
   *  emphatically not a heartbeat. */
  readonly malformedFrames: number;
  /** `type` of the last decoded event; absent when none arrived. */
  readonly lastFrameType?: string;
  /** ms since the last decoded event; absent when none arrived. */
  readonly lastFrameAgoMs?: number;
  /** A `workflow_run` call or a `workflow-progress` frame crossed this stream. */
  readonly hadWorkflow: boolean;
  /** Run id from the `workflow_run` tool's OWN result — the reliable source.
   *  Progress frames usually never cross the stream for an async run, because
   *  the tool returns in ~25ms and the stream closes long before the run ends. */
  readonly workflowRunId?: string;
}

/**
 * Thrown when the live stream is presumed dead while the turn may still be
 * running server-side: either no bytes arrived within {@link STALL_TIMEOUT_MS}
 * (the runtime heartbeats a `: ping` comment every 25 s, so a silent
 * connection is a dropped one — idle NAT/proxy kills often deliver no
 * error/FIN, the read just never resolves), or the stream hit EOF without a
 * terminal `complete`/`error` frame. The runtime persists the reply to the
 * session transcript BEFORE it sends the terminal frames, so callers should
 * recover by re-reading the transcript — never by re-sending the turn, which
 * would run it (and any workflow it started) twice.
 */
export class StreamStalledError extends Error {
  /** What the stream did before it died; see {@link StreamStats}. */
  readonly stats: StreamStats;
  constructor(message: string, stats: StreamStats) {
    super(message);
    this.name = 'StreamStalledError';
    this.stats = stats;
  }
}

/** Max silence between received bytes before the stream is declared stalled:
 *  ~3 missed server heartbeats — generous for transient congestion, short
 *  enough that a dropped connection doesn't strand the UI for minutes. */
const STALL_TIMEOUT_MS = 90_000;

/** Max time with heartbeats-but-no-real-output before we stop waiting on the
 *  live stream and reconcile from the persisted transcript. Distinct from the
 *  liveness watchdog above: the runtime's `: ping` keepalives prove the socket
 *  is ALIVE but not that the turn is making PROGRESS — a lost terminal frame
 *  leaves keepalives trickling in forever, which used to hang the client
 *  indefinitely (AndrewParisDJ beta hang, 2026-07-07). Generous enough that a
 *  legitimately slow model call (large context, minutes of thinking) is never
 *  cut short; the transcript-recovery fallback then surfaces the finished
 *  reply without a visible break. */
const NO_PROGRESS_TIMEOUT_MS = 240_000;

/**
 * Thrown when the agent rejects the turn because the user is over their daily
 * prompt cap. The streaming route signals this as a terminal
 * `daily-limit-exceeded` SSE frame on a `200` stream — *not* an HTTP 429 —
 * because AgentCore turns a non-2xx-without-stream into a `424 Failed
 * Dependency` (which we'd otherwise mis-render as a generic "temporarily
 * unavailable" outage). A distinct type so the console shows the friendly
 * limit notice with the reset time, and offers no Retry (retrying can't help
 * until the window resets). See `docs/usage-limits.md` in the agents repo.
 */
export class DailyLimitError extends Error {
  readonly limit: number;
  readonly used: number;
  readonly remaining: number;
  readonly resetAt: string;
  constructor(info: { limit: number; used: number; remaining: number; resetAt: string }) {
    super('daily-limit-exceeded');
    this.name = 'DailyLimitError';
    this.limit = info.limit;
    this.used = info.used;
    this.remaining = info.remaining;
    this.resetAt = info.resetAt;
  }
}

/** A deterministic source citation bound to the turn (from the tool
 *  transcript, independent of the model's prose). */
export interface AgentCitation {
  url: string;
  domain: string;
  toolName: string;
  toolUseId: string;
}

/** The `complete.final` envelope (the same JSON `/invocations` returns). */
export interface AgentFinal {
  agentId: string;
  output: string;
  sessionId: string;
  usage?: { inputTokens: number; outputTokens: number };
  selectedTools?: readonly string[];
  /** Deterministic source citations for the turn; present iff URL-bearing tools ran. */
  citations?: readonly AgentCitation[];
  traceId?: string;
  /** Daily budget snapshot from the runtime's usage limiter (present when a
   *  limiter is configured) — the guest console's details meter reads it. */
  usageLimit?: {
    limit: number;
    used: number;
    remaining: number;
    resetAt: string;
    omUsedToday?: number;
    omBudget?: number;
  };
}

/** A memory record as it rides the turn stream. Mirrors the runtime's
 *  `MemoryRecord`; the embedding vector (if any) is irrelevant to the graph and
 *  ignored here. */
export interface StreamedMemoryRecord {
  id: string;
  ts: string;
  kind: 'turn' | 'fact';
  text: string;
  meta?: Record<string, string>;
}

/** A decoded SSE frame — the runtime's streamed event union, plus the
 *  server-injected `memory-write` transport frame. */
export type AgentStreamEvent =
  | { type: 'text-delta'; delta: string }
  | { type: 'tool-call-start'; toolUseId: string; name: string }
  | {
      type: 'tool-call-end';
      toolUseId: string;
      name: string;
      input: Record<string, unknown>;
      output: string;
    }
  | { type: 'guardrail-blocked'; cannedOutput: string }
  | {
      // A memory mutation this turn produced, flushed before `complete`. Shape
      // matches the runtime's `MemoryMutationEvent` (spread onto the frame).
      type: 'memory-write';
      moduleId: string;
      op: 'append' | 'delete' | 'reset';
      userId: string;
      agentId: string;
      records?: readonly StreamedMemoryRecord[];
      recordIds?: readonly string[];
      ts: string;
    }
  | {
      // Records the agent pulled into context this turn (a read, not a write),
      // flushed before `complete`. Lets the graph light up the cross-tier records
      // that are in context, with their real ids. Shape mirrors `memory-write`.
      type: 'memory-recall';
      moduleId: string;
      userId: string;
      agentId: string;
      records?: readonly StreamedMemoryRecord[];
      ts: string;
    }
  | {
      // Live workflow-run progress, injected by the server while a
      // `workflow_run` tool call executes: every run-record transition plus
      // a ~30s elapsed tick. `record` is the full run record — the same
      // shape the workflow REST endpoints return — so the run card renders
      // from it directly, no polling needed while these flow.
      type: 'workflow-progress';
      displayName: string;
      elapsedMs: number;
      ts: string;
      record: WorkflowRunRecord;
    }
  | {
      // Live "what the agent is doing now" for the UI activity line: model
      // thinking, a running tool, or the reviewer verifying facts. Carries no
      // answer text and no reviewer critique — just a friendly label so a long
      // turn never looks stuck.
      type: 'status';
      phase: 'thinking' | 'tool' | 'verifying' | 'reworking' | 'finalizing';
      label: string;
    }
  | { type: 'complete'; final: AgentFinal }
  | { type: 'error'; error: string; message: string }
  // Terminal frame: this `turnId` was already accepted, so the runtime refused
  // to run it a second time. `streamAgentTurn` converts it into a
  // `StreamStalledError` — the reply is landing in the transcript, which is
  // exactly the case transcript reconciliation already handles.
  | { type: 'turn-duplicate'; message?: string }
  // Terminal frame: the caller is over their daily prompt cap. Rides a 200 SSE
  // stream (see DailyLimitError); `streamAgentTurn` converts it into a thrown
  // `DailyLimitError` rather than dispatching it to `onEvent`.
  | { type: 'daily-limit-exceeded'; limit: number; used: number; remaining: number; resetAt: string };

export interface StreamTurnInput {
  /** Agent dispatcher key, e.g. `uno`. */
  readonly agentId: string;
  /** The user's message for this turn. */
  readonly input: string;
  /** Conversation session id (33–100 chars), stable across a conversation. */
  readonly sessionId: string;
  /**
   * Id for THIS user turn, stable across retries of the same turn. The runtime
   * claims it before running anything and refuses a second claim, which is what
   * makes a retry safe: a failure before the response headers is
   * indistinguishable from "never delivered", so without this a retry could run
   * the turn — and any workflow it started — twice. A NEW turn must use a new id.
   */
  readonly turnId?: string;
  /** Aborts the in-flight request when the turn is cancelled. */
  readonly signal: AbortSignal;
  /**
   * Endpoint override for non-Cognito callers (the GUEST proxy, tiers T8):
   * a fixed URL + a pre-minted bearer. Omitted → the signed-in default
   * (`agentConfig.invokeUrl` + the Cognito access token).
   */
  readonly endpoint?: { readonly invokeUrl: string; readonly jwt: string };
}

const SESSION_HEADER = 'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id';

/**
 * A fresh id for ONE user turn, stable across that turn's retries. Format
 * matches the runtime's accepted shape (`[A-Za-z0-9_-]{8,128}`).
 */
export function newTurnId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `t-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/** A fresh session id of the form `<agentId>-<32 hex>` (36 chars, ≥33). */
export function newSessionId(agentId: string): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${agentId}-${hex}`;
}

/**
 * Translate a failed agent HTTP response into a user-facing message. Never
 * leaks internal infrastructure (CloudWatch, AgentCore/runtime internals, raw
 * backend bodies) — those go to the console for developers.
 *
 * The runtime rejects an under-scoped token with `403`, which AgentCore wraps
 * as a `424` whose body embeds `(403) from runtime`. Both mean the signed-in
 * user's token can't invoke this agent — almost always a stale token missing
 * the group/scope claim, which re-authenticating mints fresh.
 */
function describeAgentError(status: number, body: string): string {
  const permissionDenied =
    status === 403 || (status === 424 && /\b403\b/.test(body));
  if (permissionDenied) {
    return "You don't have permission to chat with this agent. Please sign out and sign back in, then try again.";
  }
  if (status === 401) {
    return 'Your session has expired. Please sign in again, then retry.';
  }
  return 'The agent is temporarily unavailable. Please try again in a moment.';
}

/**
 * The stream's frame sink: where decoded frames go, and where a failure gets
 * its diagnosis from.
 *
 * One cohesive object rather than two loose callbacks — `consumeEventStream`
 * already takes four parameters, and the two inner throw sites need the
 * snapshot as much as the loop needs the recorder.
 */
interface FrameSink {
  /** Record a decoded frame (and re-arm the progress watchdog for a real one). */
  readonly onFrame: (frame: DecodedFrame) => void;
  /** Capture what the stream had done, as of now. */
  readonly snapshot: (reason: StreamFailureReason) => StreamStats;
}

/**
 * Running tally of what a stream delivered, snapshotted into a
 * {@link StreamStats} whenever the stream fails.
 *
 * Kept as a closure rather than threaded through `consumeEventStream` as extra
 * parameters: that function already takes four callbacks, and a fifth would be
 * exactly the positional sprawl a props-object exists to prevent. It sees every
 * decoded frame through the one callback it already had.
 */
function createStreamStats(): {
  record: (frame: DecodedFrame) => void;
  snapshot: (reason: StreamFailureReason) => StreamStats;
} {
  const startedAt = Date.now();
  const frames: Record<string, number> = {};
  let frameCount = 0;
  let keepalives = 0;
  let malformedFrames = 0;
  let lastFrameType: string | undefined;
  let lastFrameAt: number | undefined;
  let hadWorkflow = false;
  let workflowRunId: string | undefined;

  return {
    record: (frame: DecodedFrame): void => {
      if (frame.kind === 'keepalive') {
        keepalives += 1;
        return;
      }
      if (frame.kind === 'malformed') {
        malformedFrames += 1;
        return;
      }
      const event = frame.event;
      frames[event.type] = (frames[event.type] ?? 0) + 1;
      frameCount += 1;
      lastFrameType = event.type;
      lastFrameAt = Date.now();
      if (event.type === 'workflow-progress') {
        hadWorkflow = true;
        workflowRunId ??= event.record.runId;
        return;
      }
      // The tool's own result is the RELIABLE source of the run id: an async
      // run's progress frames usually never cross this stream at all, because
      // `workflow_run` returns in ~25ms and the turn ends long before the run.
      if (event.type === 'tool-call-end' && event.name === 'workflow_run') {
        hadWorkflow = true;
        workflowRunId ??= runIdFromToolResult(event.output);
      }
    },
    snapshot: (reason: StreamFailureReason): StreamStats => ({
      reason,
      elapsedMs: Date.now() - startedAt,
      frames: { ...frames },
      frameCount,
      keepalives,
      malformedFrames,
      hadWorkflow,
      ...(lastFrameType !== undefined ? { lastFrameType } : {}),
      ...(lastFrameAt !== undefined ? { lastFrameAgoMs: Date.now() - lastFrameAt } : {}),
      ...(workflowRunId !== undefined && workflowRunId !== '' ? { workflowRunId } : {}),
    }),
  };
}

/** Pull the run id out of a `workflow_run` tool result, if it names one. */
function runIdFromToolResult(output: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(output);
    if (typeof parsed === 'object' && parsed !== null) {
      const runId = (parsed as Record<string, unknown>)['runId'];
      if (typeof runId === 'string' && runId.length > 0) {
        return runId;
      }
    }
  } catch {
    // Not JSON — the tool failed or returned prose. No run to correlate; the
    // caller's `hadWorkflow` still records that a workflow was attempted.
  }
  return undefined;
}

/**
 * Run one chat turn, invoking `onEvent` for each parsed stream frame. Resolves
 * on the terminal `complete`/`error` frame; rejects on a transport/HTTP error,
 * on abort, or with {@link StreamStalledError} when the connection goes silent
 * past the watchdog window or ends without a terminal frame (the turn may
 * still be running server-side — see the error's doc). Non-JSON frames
 * (keepalive comments) are skipped, but any received bytes re-arm the
 * watchdog.
 */
export async function streamAgentTurn(
  input: StreamTurnInput,
  onEvent: (event: AgentStreamEvent) => void,
): Promise<void> {
  const jwt = input.endpoint?.jwt ?? (await getAccessToken());

  // Stall watchdog. `watchdog` aborts the network request; `stall` rejects
  // the pending fetch/read race so the failure surfaces deterministically
  // even where an abort doesn't propagate to an in-flight body reader.
  const watchdog = new AbortController();
  const onCallerAbort = (): void => watchdog.abort();
  if (input.signal.aborted) {
    watchdog.abort();
  } else {
    input.signal.addEventListener('abort', onCallerAbort, { once: true });
  }
  let stallReject: (err: Error) => void = () => {};
  const stall = new Promise<never>((_, reject) => {
    stallReject = reject;
  });
  const stats = createStreamStats();
  let stallTimer: ReturnType<typeof setTimeout> | undefined;
  const armStallTimer = (): void => {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => {
      watchdog.abort();
      stallReject(
        new StreamStalledError(
          `No data from the agent stream in ${STALL_TIMEOUT_MS / 1000}s — connection presumed dead.`,
          stats.snapshot('stalled-no-bytes'),
        ),
      );
    }, STALL_TIMEOUT_MS);
  };
  // Re-armed ONLY when a real event arrives (not on keepalives) — see
  // NO_PROGRESS_TIMEOUT_MS. Fires the same recovery path as the liveness
  // watchdog, but survives a heartbeats-only connection.
  let progressTimer: ReturnType<typeof setTimeout> | undefined;
  const armProgressTimer = (): void => {
    clearTimeout(progressTimer);
    progressTimer = setTimeout(() => {
      watchdog.abort();
      stallReject(
        new StreamStalledError(
          `No agent output in ${NO_PROGRESS_TIMEOUT_MS / 1000}s — reconciling from the transcript.`,
          stats.snapshot('stalled-no-progress'),
        ),
      );
    }, NO_PROGRESS_TIMEOUT_MS);
  };

  try {
    armStallTimer();
    armProgressTimer();
    const response = await Promise.race([
      fetch(input.endpoint?.invokeUrl ?? agentConfig.invokeUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwt}`,
          [SESSION_HEADER]: input.sessionId,
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentId: input.agentId,
          input: input.input,
          sessionId: input.sessionId,
          ...(input.turnId !== undefined ? { turnId: input.turnId } : {}),
          _jwt: jwt,
        }),
        signal: watchdog.signal,
      }),
      stall,
    ]);
    armStallTimer();

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      // The raw backend body names internal infrastructure (CloudWatch, runtime
      // internals) — keep it for developers in the console, never show it to the
      // user. The user sees a plain-language, actionable message instead.
      console.error(`Agent request failed (${response.status})`, detail);
      const message = describeAgentError(response.status, detail);
      if (response.status === 401) {
        throw new SessionExpiredError(message);
      }
      throw new Error(message);
    }
    if (!response.body) {
      throw new Error('Agent response had no body to stream');
    }

    const reader = response.body.getReader();
    try {
      const sink: FrameSink = {
        onFrame: (frame) => {
          stats.record(frame);
          // Only a real event is PROGRESS. A keepalive proves the socket is
          // alive (that is the other watchdog's job) and a malformed frame
          // proves the runtime is misbehaving — neither means the turn advanced.
          if (frame.kind === 'event') {
            armProgressTimer();
          }
        },
        snapshot: stats.snapshot,
      };
      await consumeEventStream(reader, stall, onEvent, armStallTimer, sink);
    } catch (err) {
      // Past this point the server has ACCEPTED the turn (headers returned OK)
      // and is running it. A transport failure while reading the body — socket
      // reset, Wi-Fi/NAT change, proxy kill — surfaces as a bare `TypeError`,
      // which callers would otherwise classify as "the message didn't get
      // through" and offer a Retry that RE-RUNS the turn (and any workflow it
      // started) a second time. It's a dead stream over a live turn, so report
      // it as one: the caller reconciles from the persisted transcript instead.
      // Aborts stay verbatim — the caller cancelled, nothing to recover.
      if (
        err instanceof StreamStalledError ||
        err instanceof DailyLimitError ||
        input.signal.aborted
      ) {
        throw err;
      }
      throw new StreamStalledError(
        `The agent stream dropped while the turn was running (${
          err instanceof Error ? err.message : String(err)
        }).`,
        stats.snapshot('transport-dropped'),
      );
    } finally {
      reader.releaseLock();
    }
  } finally {
    clearTimeout(stallTimer);
    clearTimeout(progressTimer);
    input.signal.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * Read the SSE body to its terminal frame, dispatching each decoded event.
 * Returns once `complete`/`error` arrives; throws {@link StreamStalledError} if
 * the body ends without one, or {@link DailyLimitError} on the cap frame.
 * `onBytes` re-arms the liveness watchdog (any traffic, keepalives included);
 * `onProgress` re-arms the no-progress watchdog (real frames only).
 */
async function consumeEventStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  stall: Promise<never>,
  onEvent: (event: AgentStreamEvent) => void,
  onBytes: () => void,
  sink: FrameSink,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await Promise.race([reader.read(), stall]);
    if (done) {
      break;
    }
    onBytes();
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are blank-line delimited; a frame's `data:` lines join with
    // newlines. Keep the trailing partial frame until its separator arrives.
    let sep = buffer.indexOf('\n\n');
    while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (dispatchFrame(parseFrame(frame), onEvent, sink)) {
        return;
      }
      sep = buffer.indexOf('\n\n');
    }
  }
  // Flush a final frame that arrived without a trailing blank line. A blank
  // remainder is NOT such a frame — every well-formed stream ends with one,
  // and dispatching it would tally a phantom keepalive on every single turn.
  if (buffer.trim() !== '' && dispatchFrame(parseFrame(buffer), onEvent, sink)) {
    return;
  }
  // The contract terminates every stream with a `complete`/`error` frame;
  // EOF without one means the connection was cut mid-turn (proxy FIN,
  // dropped socket) — the turn itself may still be running server-side.
  throw new StreamStalledError(
    'Agent stream ended without a terminal frame.',
    sink.snapshot('eof-no-terminal'),
  );
}

/** Deliver one decoded frame; true when it terminates the stream. */
function dispatchFrame(
  decoded: DecodedFrame,
  onEvent: (event: AgentStreamEvent) => void,
  sink: FrameSink,
): boolean {
  // Every frame is tallied, including keepalives and malformed ones — the
  // counts are the difference between "the socket was idle" and "the runtime
  // was emitting garbage".
  sink.onFrame(decoded);
  if (decoded.kind !== 'event') {
    return false;
  }
  const event = decoded.event;
  if (event.type === 'daily-limit-exceeded') {
    throw new DailyLimitError(event);
  }
  if (event.type === 'turn-duplicate') {
    // The runtime already accepted this turn under the same id and declined to
    // run it twice — precisely the "live turn, no live stream" situation, so
    // reuse the stall path and reconcile from the persisted transcript.
    throw new StreamStalledError(
      event.message ?? 'This turn was already accepted; recovering it from the transcript.',
      sink.snapshot('turn-duplicate'),
    );
  }
  onEvent(event);
  return event.type === 'complete' || event.type === 'error';
}

/**
 * One decoded SSE frame.
 *
 * Three outcomes, deliberately distinguished. This used to be
 * `AgentStreamEvent | null`, and `null` covered FOUR different situations — a
 * keepalive comment, unparseable JSON, a non-object payload, and an object with
 * no `type`. Collapsing them meant a runtime emitting corrupt frames was
 * indistinguishable from a healthy heartbeat stream, so a real defect would
 * have been counted as proof of life (PRINCIPLES §12: a catch must not convert
 * one error into a different, benign one).
 */
type DecodedFrame =
  | { readonly kind: 'event'; readonly event: AgentStreamEvent }
  /** A comment/blank frame — the runtime's `: ping` liveness beat. */
  | { readonly kind: 'keepalive' }
  /** Carried `data:` we could not turn into an event. A server-side defect. */
  | { readonly kind: 'malformed' };

/** Parse one SSE frame (its `data:` lines). See {@link DecodedFrame}. */
function parseFrame(frame: string): DecodedFrame {
  const dataLines: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) {
    return { kind: 'keepalive' };
  }
  let raw: unknown;
  try {
    raw = JSON.parse(dataLines.join('\n'));
  } catch {
    return { kind: 'malformed' };
  }
  if (typeof raw !== 'object' || raw === null) {
    return { kind: 'malformed' };
  }
  const value = raw as Record<string, unknown>;
  return typeof value.type === 'string'
    ? { kind: 'event', event: value as unknown as AgentStreamEvent }
    : { kind: 'malformed' };
}
